import { useState, useEffect, useRef } from 'react';
import { FileStatus, SnapshotStatus } from '../data/enums';
import { S3Client, ListObjectsV2Command, GetObjectCommand, ListObjectsV2Output } from "@aws-sdk/client-s3";
import { useAuth } from '../context/auth-provider';
import { Upload } from '@aws-sdk/lib-storage';
import { trackTelemetryEvent, getUserGroupsFromIdToken } from '../utils/telemetry';

const S3_MAX_KEYS_TO_LIST = 100;

interface IuseS3ManagerProps {
  bucketName: string;
  manifestItems?: Array<IManifestItem> | null | undefined; 
  selectedClient?: string | null | undefined;
  snapshotDate?: string | null | undefined;
}

interface IuseS3ManagerResult {
  isS3initialized: boolean;
  fileUploads: Array<IFileUpload> | null;
  uploadAll: () => Promise<boolean>;
  uploadSingle: (category: string) => void;  //Promise<IFileUpload>
  isProcessing: boolean;
  setFileForUpload: (category: string, file: File | null) => void;
  listObjectsAtPrefix: (prefix: string) => Promise<Array<IFileUpload>>;
  downloadObject: (key: string, customFilename?: string) => Promise<void>;
  downloadJsonFile: (key: string) => Promise<string>;
  getObjectsForBucket: (bucket: string, prefix: string) => Promise<Array<string>>;
}

export const useS3Manager = (props: IuseS3ManagerProps): IuseS3ManagerResult => {

  const s3Ref = useRef<any | null>();
  const [isS3initialized, setIsS3initialized] = useState<boolean>(false);
  const [fileUploads, setFileUploads] = useState<Array<IFileUpload> | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const { user, refresh } = useAuth();

  // useEffect(() => {

  //   try {

  //     console.log(`useS3Manager.useEffect :: mount`);
  //     const credentials = getCredentials()
  //     if (!credentials) {
  //       // console.error(`useS3Manager.useEffect :: credentials not found`);
  //       return;
  //     }

  //     credentials.get(function(err:any) {        
  //       s3Ref.current = new S3Client({
  //         region: process.env.REACT_APP_AWS_REGION!,
  //         credentials: {
  //           accessKeyId: credentials.accessKeyId,
  //           secretAccessKey: credentials.secretAccessKey,
  //           sessionToken: credentials.sessionToken 
  //         }
  //       });
  //       setIsS3initialized(true);
  //       // console.log(`useS3Manager.useEffect :: mount :: s3Ref initalized`);
  //     });
      
  //   } catch(e) {
  //     console.error(`useS3Manager.useEffect :: error -  ${e}`);
  //   }

  //   // return () => { console.log(`useS3Manager.useEffect :: unmount`); };

  // }, []);

  useEffect(() => {
    const initS3 = async () => {
      if (!user || !user.accessKeyId || !user.secretAccessKey || !user.sessionToken) {
        await refresh(); // Force a session/credential refresh
      }

      if (!user?.accessKeyId || !user.secretAccessKey || !user.sessionToken) {
        console.error('useS3Manager :: refresh failed or user still invalid');
        return;
      }

      s3Ref.current = new S3Client({
        region: process.env.REACT_APP_AWS_REGION!,
        credentials: {
          accessKeyId: user.accessKeyId,
          secretAccessKey: user.secretAccessKey,
          sessionToken: user.sessionToken
        },
        // Prevent automatic checksum opt-in on multipart create unless explicitly required.
        requestChecksumCalculation: 'WHEN_REQUIRED' as any,
        responseChecksumValidation: 'WHEN_REQUIRED' as any,
      });

      setIsS3initialized(true);
    };

    initS3().catch((err) => {
      console.error('useS3Manager.initS3 :: error during credential refresh:', err);
    });
  }, [user, refresh]);

  useEffect(() => {
    
    if (isProcessing) return;

    if (!props.manifestItems) {

      setFileUploads([]);

    } else {

      const files : Array<IFileUpload> = props.manifestItems.map((item, index) => ({ 
        ...item, 
        FileStatus: getFileStatus(item),
        FileForUpload: null,
        FileSize: 0,
        UploadProgress: 0
      }));

      files.sort((a, b) => {
        if (a.FileStatus && b.FileStatus) return a.FileStatus.localeCompare(b?.FileStatus);
        return 0;        
      });

      setFileUploads(files);
    }

  }, [props.manifestItems]);

  //TODO: switch to return void
  const uploadAll = async (): Promise<boolean> => {    

    if (!user) {
      console.error(`useS3Manager.uploadAll :: ERROR :: user not found`);
      return false;
    }


    if (!fileUploads) return false;
    const pendingUploads = fileUploads.filter(item => item.FileStatus === FileStatus.Collected );
    const uploads = [] as Promise<IFileUpload>[];

    pendingUploads?.forEach((file, index) => {      
      console.log(`useS3Manager.uploadAll :: file: ${file.Category} - ${file.FileStatus}`);
      uploads.push(uploadSingle(file.Category));    
    });

    //promise all settled
    const results = await Promise.allSettled(uploads);

    // resolved/fulfilled Promises' values
    const fulfilled = results.filter((result): result is PromiseFulfilledResult<IFileUpload> => result.status === 'fulfilled').map(result => result.value)
    
    // rejected Promises' reasons
    const rejected = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected').map(result => result.reason)
    
    if (rejected.length>0 && fulfilled.length==0) {
      // all failed
      console.error(`useS3Manager.uploadAll :: ERROR :: all uploads failed!`);
      console.log(rejected)
      return false;

    } else if (rejected.length>0 && fulfilled.length>0) {
      // some failed
      console.error(`useS3Manager.uploadAll :: ERROR :: [${rejected.length}] uploads failed!`);
      console.log(rejected) 
      //console.log(fulfilled)
      return true;

    } else if (rejected.length==0 && fulfilled.length>0) {
      // all success
      console.log(`useS3Manager.uploadAll :: all uploads complete`);
      return true;
    }

    return false;    
  };

  const uploadSingle = async (category: string): Promise<IFileUpload> => {
      
      if (!fileUploads) {
        //console.log(`useS3Manager.uploadSingle - fileUploads not found`);
        return { Category: category, FileStatus: FileStatus.Error, FileSize: 0, UploadProgress: 0 };
      }

      //console.log(`useS3Manager.uploadSingle - category: ${category}`);
      const pendingUpload = fileUploads!.filter(item => item.Category === category ).pop();
      if (!pendingUpload) {
        //console.log(`useS3Manager.uploadSingle - pendingUpload at category [${category}] not found`);
        return { Category: category, FileStatus: FileStatus.Error, FileSize: 0, UploadProgress: 0 };
      } 

      const params = {
          Bucket: props.bucketName,
          Key: getS3Key(category,props.snapshotDate!,props.selectedClient!,pendingUpload!.FileForUpload!), //`ama_${props.selectedClient}/snapshot=${props.snapshotDate}/hash_pii/${category}`,//data lake key //preprocess_path = f'ama_{client_name}/snapshot={snapshot_date}/hash_pii/'  
          Body: pendingUpload!.FileForUpload!,//file from manaifest builder
          type: pendingUpload!.FileForUpload!.type,
      };

      const startedAt = performance.now();
      try {
        const upload = new Upload({
          client:s3Ref.current,
          params:params
        });
        upload.on("httpUploadProgress", (progress:any) => {
          setFileUploadStatus(category, progress.loaded/progress.total);
        });

        //init file upload status while upload client starts
        setFileUploadStatus(category, 0);
        console.log(`useS3Manager.uploadSingle :: calling done`);
        const completeMultiPartUpload = await upload.done();
        console.log(`useS3Manager.uploadSingle :: done`);
        upload.removeAllListeners();

        if (user) {
          trackTelemetryEvent({
            userSub: user.userId,
            userEmail: user.email,
            userGroups: getUserGroupsFromIdToken(user.idToken),
            authToken: user.idToken,
            actionType: 'file_upload',
            actionTarget: params.Key,
            status: 'success',
            latencyMs: Math.round(performance.now() - startedAt),
          });
        }

        return { Category: category, FileStatus: FileStatus.Uploading, FileSize: pendingUpload!.FileSize, UploadProgress: 100 };
      } catch (error: any) {
        if (user) {
          trackTelemetryEvent({
            userSub: user.userId,
            userEmail: user.email,
            userGroups: getUserGroupsFromIdToken(user.idToken),
            authToken: user.idToken,
            actionType: 'file_upload',
            actionTarget: params.Key,
            status: 'failure',
            latencyMs: Math.round(performance.now() - startedAt),
            extra: { error: String(error?.message || error) },
          });
        }
        throw error;
      }
  };

  // TODO - refactor - new type instead of IFileUpload, more generic - otherwise i'm donwloading silver by referencing bronzepath
  const listObjectsAtPrefix = async (prefix:string): Promise<Array<IFileUpload>> => {

    const command = new ListObjectsV2Command({
      Bucket: props.bucketName,
      MaxKeys: S3_MAX_KEYS_TO_LIST,
      Prefix: prefix
    });

    let isTruncated = true;
    const s3KeysArr = [] as Array<IFileUpload>;
    while (isTruncated) {
      const { Contents, IsTruncated, NextContinuationToken } = await s3Ref.current.send(command);
      isTruncated = IsTruncated;
      command.input.ContinuationToken = NextContinuationToken;
      if(!Contents){
        console.error(`useS3Manager.listObjectsAtPrefix :: Contents not found`);
      } else{
        Contents.forEach( (c:any) => {
          s3KeysArr.push({ Category: c.Key.split('/').pop(), BronzePath:c.Key, FileStatus: FileStatus.Collected, FileSize:c.Size, UploadProgress:0 });
        });
      }

    }
    return s3KeysArr;

  };

  // const downloadObject = async (key: string): Promise<void> => {

  //   const params = {
  //     Bucket: props.bucketName,
  //     Key: key,
  //   };
  //   const command = new GetObjectCommand(params);
  //   const data = await s3Ref.current.send(command);
    
  //   //convert a readable stream to a blob
  //   const response = new Response(data.Body);
  //   const blob = await response.blob();
  //   downloadBlob(blob, key.split('/').pop() as string);
  //   return;
  // };
  const downloadObject = async (key: string, customFilename?: string): Promise<void> => {
    
    const params = {
      Bucket: props.bucketName,
      Key: key,
    };
    
    const command = new GetObjectCommand(params);
    const data = await s3Ref.current.send(command);
    
    // Convert a readable stream to a blob
    const response = new Response(data.Body);
    const blob = await response.blob();
    
    // Use custom filename if provided, otherwise extract from key
    const filename = customFilename || key.split('/').pop() as string;
    downloadBlob(blob, filename);

    return;
  };

  const downloadJsonFile = async (key: string): Promise<string> => {

    let result = '';
    if (!s3Ref.current) return result;
    const params = { Bucket: props.bucketName, Key: key };

    try {

      const command = new GetObjectCommand(params);
      const data = await s3Ref.current.send(command);
      result = await new Response(data.Body).text();
      
    } catch (error) {
      console.log(`useS3Manager.downloadJsonFile :: error: ${error}`)
    }

    return result;
  };
  
  const setFileForUpload = (category: string, file: File | null) => {
    if (!category || !fileUploads) return;
    const files = [...fileUploads];
    //get index of file category
    const index = files.findIndex(file => file.Category === category);
    //return if index not found
    if(index<0){
      console.error(`useS3Manager.setFileForUpload :: index not found for ${category}`);
      return;
    }  
    files[index].FileForUpload = file;
    files[index].FileStatus = (file) ? FileStatus.Collected : FileStatus.Empty;
    //console.log(`useS3Manager.setFileForUpload :: updating ${category} with file status ${files[index].FileStatus}`);
    setFileUploads(files);
  };

  const setFileUploadStatus = (category: string, uploadProgress: number) => {
    const COMPLETED_UPLOAD_PROGRESS = 100.0;
    if (!category || !fileUploads) return;
    const files = [...fileUploads];
    //get index of file category
    const index = files.findIndex(file => file.Category === category);
    //return if index not found
    if (index < 0) {
      console.error(`useS3Manager.setFileUploadStatus :: index not found for ${category}`);
      return;
    }  

    files[index].UploadProgress = (Math.round(uploadProgress * 100) / 100) * 100;
    // files[index].FileStatus = (files[index].UploadProgress < COMPLETED_UPLOAD_PROGRESS) ? FileStatus.Uploading : FileStatus.Uploaded;
    files[index].FileStatus = FileStatus.Uploading;
    console.log(`useS3Manager.setFileUploadStatus :: updating ${category} at ${files[index].UploadProgress}%`);
    setFileUploads(files);
  };

  const getObjectsForBucket = async (bucket: string, prefix: string): Promise<Array<string>> => {
    
    if (!s3Ref.current) return [];

    const fileAndFolderNames: string[] = [];
    let continuationToken: string | undefined = undefined;    

    do {

      const params = {
        Bucket: bucket,
        Prefix: prefix,
        ContinuationToken: continuationToken,
      };

      console.log(`useS3Manager.getObjectsForBucket :: path: ${bucket}/${prefix}`);
  
      try {

        const command = new ListObjectsV2Command(params);
        const result: ListObjectsV2Output = await s3Ref.current.send(command);

        // Extract file and folder names from the result
        const objects = result.Contents || [];
        const names = objects.map(object => object.Key || '');
        fileAndFolderNames.push(...names);
  
        // Set the continuation token for the next iteration
        continuationToken = result.NextContinuationToken;

      } catch (error) {
        console.error(`useS3Manager.getObjectsForBucket :: Error listing files and folders: ${error}`);
        return fileAndFolderNames;
      }
    } while (continuationToken);  

    return fileAndFolderNames;
  };

  return {
    isS3initialized,
    uploadAll,
    fileUploads,
    uploadSingle,
    isProcessing,
    setFileForUpload,
    downloadObject,
    downloadJsonFile,
    listObjectsAtPrefix,
    getObjectsForBucket
  };
};


const getS3Key = (category: string, snapshotDate: string, clientName: string, fileForUpload: File ) => {
  
  // get file extension
  const file_extension = fileForUpload.name.split('.').pop();
  //console.log(`useS3Manager.gets3Key - category: ${category} - snapshotDate: ${snapshotDate} - clientName: ${clientName} - file_extension: ${file_extension}`);

  //return key based on file extension, error if file not supported
  if (file_extension==='csv') {
    return `ama_${clientName}/snapshot=${snapshotDate}/hash_pii/${category}/${category}.${file_extension}`;
  } else if (file_extension==='xlsx') {
    return `ama_${clientName}/snapshot=${snapshotDate}/convert_file/${category}/${category}.${file_extension}`;
  } else {
    //error state - we only except csv and xlsx
    console.error(`useS3Manager.gets3Key - file extension not supported: ${file_extension}`);
    return ``;
  }
}

const downloadBlob = (blob: Blob, filename: string) => {

  // Convert your blob into a Blob URL (a special url that points to an object in the browser's memory)
  const blobUrl = URL.createObjectURL(blob);
  // Create a link element
  const link = document.createElement('a');
  // Set link's href to point to the Blob URL
  link.href = blobUrl;
  //TODO: Set file extension
  // link.download = filename+'.csv';
  link.download = filename;
  // Append link to the body
  document.body.appendChild(link);
  // Dispatch click event on the link
  // This is necessary as link.click() does not work on the latest firefox
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );

  // Remove link from body
  document.body.removeChild(link);
}

const getTempStatusForIndex = (index: number) : FileStatus => {
  // if (index % 5 === 0) {
  //   return FileStatus.Empty;
  // } else if (index % 5 === 1) {
  //   return FileStatus.Collected;
  // } else if (index % 5 === 2) {
  //   return FileStatus.Uploading;
  // } else if (index % 5 === 3) {
  //   return FileStatus.Uploaded;
  // } else {
  //   return FileStatus.Error;
  // }
  return FileStatus.Empty;
};

const getFileStatus = (item: IManifestItem) : string => {

  // File has passed through preprocessing and saved to Bronze
  if (item.IsCollectionComplete) return FileStatus.Uploaded;

  // When back-end status is BronzeCollection, client status will move through following status': Empty, Collected, Uploading
  if (item.Status === SnapshotStatus.BronzeCollecting) return FileStatus.Empty;
  
  // File has been uploaded to preprocessing bucket but not yet in Bronze
  if (item.Status === SnapshotStatus.BronzeIngesting) return FileStatus.Processing;

  // Error states
  if (item.Status === SnapshotStatus.BronzeCollectError) return FileStatus.Error;
  if (item.Status === SnapshotStatus.BronzeIngestError) return FileStatus.Error;

  // Default to empty
  return FileStatus.Empty;
};